import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatOption, MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, RouterModule } from '@angular/router';

import { PoSelect } from '@lib/po-select';
import { ProductForm } from '@lib/product-form';

import { Product, ProductStatuses } from '../schemas';
import { Auth, ClientService, Jira, ProductService, UserService } from '../service';

const STATUS_KEY = 'cm:product:filter:statuses';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './products-page.html',
  imports: [
    FormsModule,
    MatButtonModule,
    MatFormField,
    MatFormFieldModule,
    MatIcon,
    MatInputModule,
    MatLabel,
    MatOption,
    MatPaginatorModule,
    MatSelectModule,
    MatInputModule,
    MatSortModule,
    MatTableModule,
    MatToolbarModule,
    MatTooltipModule,
    RouterModule,
    PoSelect,
  ],
})
export class ProductsPage {
  authService = inject(Auth);
  clientService = inject(ClientService);
  dialog = inject(MatDialog);
  jira = inject(Jira);
  productService = inject(ProductService);
  route = inject(ActivatedRoute);
  userService = inject(UserService);

  sort = viewChild.required(MatSort);
  paginator = viewChild.required(MatPaginator);

  clientCode = input<string>('');
  clientName = computed(() => this.clientService.names()[this.clientCode()]);

  pending = this.productService.pending;

  statusOptions = signal(ProductStatuses);

  columnsToShow = signal(['ClientCode', '_ClientName', 'JobNumber', 'Title', 'Status', 'JiraTitle', 'actions']);

  filter = signal('');
  filterPo = signal('');
  filterStatus = linkedSignal<string[]>(() => {
    const defaultStatuses = this.statusOptions().filter((v) => v !== 'Prospective');
    const storedStatuses = window.localStorage.getItem(STATUS_KEY);

    if (storedStatuses) {
      try {
        return JSON.parse(storedStatuses);
      } catch {
        return defaultStatuses;
      }
    } else {
      return defaultStatuses;
    }
  });

  products = computed(() => {
    let products = this.productService.products();
    const mapping = this.clientService.names();
    const po = this.filterPo();

    if (this.clientCode()) {
      products = products.filter((it) => it.ClientCode === this.clientCode());
    }

    const statuses = this.filterStatus();
    if (statuses.length) {
      products = products.filter(({ Status }) => statuses.includes(Status ? Status : ''));
    }

    if (po) {
      products = products.filter(({ ProductOwner }) => ProductOwner === po);
    }

    products.forEach((p) => (p['_ClientName'] = mapping[p.ClientCode]));
    return products;
  });

  source = computed(() => {
    const source = new MatTableDataSource(this.products());
    source.sort = this.sort();
    source.paginator = this.paginator();
    source.filter = this.filter();
    return source;
  });

  trackBy(idx: number, row: Product) {
    return row.JobNumber;
  }

  constructor() {
    effect(() => {
      window.localStorage.setItem('cm:product:filter:statuses', JSON.stringify(this.filterStatus()));
    });
  }

  reload() {
    this.productService.reload();
  }

  edit(product?: Product) {
    const data = product ? { product, title: 'Update product' } : {};
    this.dialog
      .open(ProductForm, {
        data,
      })
      .afterClosed()
      .subscribe({
        next: (result: Product | undefined) => {
          if (result) {
            this.reload();
          }
        },
      });
  }
}
